<template>
  <a class="feature-card" :class="[sizeClass, customClass]" :style="customStyle" @click.prevent="$emit('select', card)">
    <view class="card-icon">
      <img v-if="iconDisplay.type === 'img'" :class="['card-icon-img','']" :src="iconDisplay.src" />
      <span v-else v-html="iconDisplay.value"></span>
    </view>
    <h3 class="card-title">{{ card?.title || card?.name }}</h3>
    <p class="card-description">{{ card?.description ||  card?.desc }}</p>
    <view v-if="showArrow" class="card-arrow">
      <img v-if="arrowDisplay.type === 'img'" :src="arrowDisplay.src" class="arrow-img" />
      <span v-else v-html="arrowDisplay.value"></span>
    </view>
  </a>
</template>

<script setup>
import { computed } from 'vue'
import { resolveIcon } from '@/utils/icon.js'

const props = defineProps({
  card: {
    type: Object,
    required: true
  },
  size: {
    type: String,
    default: 'medium',
    validator: (v) => ['small', 'medium', 'large'].includes(v)
  },
  showArrow: {
    type: Boolean,
    default: true
  },
  arrow: {
    type: String,
    default: '→'
  },
  customClass: {
    type: String,
    default: ''
  },
  customStyle: {
    type: Object,
    default: () => ({})
  }
})

defineEmits(['select'])

const iconDisplay = computed(() => resolveIcon(props.card.icon))

const arrowDisplay = computed(() => {
  const arrow = props.arrow
  if (!arrow) return { type: 'text', value: '→' }
  if (/\.(png|svg|jpg|jpeg|gif|webp|ico)$/i.test(arrow)) {
    return { type: 'img', src: arrow }
  }
  return { type: 'text', value: arrow }
})

const sizeClass = computed(() => `feature-card--${props.size}`)
const iconClass = computed(() => `card-icon-img--${props.size}`)
</script>

<style scoped>
/* ========== 基础样式 ========== */
.feature-card {
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  background: var(--glass-bg, rgba(255, 255, 255, 0.08));
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border: 1px solid var(--glass-border, rgba(255, 255, 255, 0.1));
  border-radius: 14px;
  box-shadow: 0 4px 24px var(--glass-shadow, rgba(0, 0, 0, 0.06));
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.4s ease, border-color 0.4s ease;
}

/* ========== 顶部高光线 ========== */
.feature-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, transparent, rgba(91, 141, 239, 0.6), transparent);
  opacity: 0;
  transition: opacity 0.3s;
}

.feature-card:hover::before {
  opacity: 1;
}

.feature-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 32px var(--glass-shadow, rgba(0, 0, 0, 0.1));
}

/* ========== 尺寸预设 ========== */
/* small */
.feature-card--small {
  padding: 12px 14px;
  height: 120px;
}

.feature-card--small .card-icon {
  font-size: 20px;
  margin-bottom: 10px;
}

.feature-card--small .card-icon-img {
  width: 40px;
  height: 40px;
}

.feature-card--small .card-title {
  font-size: 13px;
  margin-bottom: 4px;
}

.feature-card--small .card-description {
  font-size: 11px;
  line-height: 1.4;
}

.feature-card--small .card-arrow {
  font-size: 12px;
  margin-top: 4px;
}

.feature-card--small .arrow-img {
  width: 14px;
  height: 14px;
}

/* medium (默认) */
.feature-card--medium {
  padding: 18px 18px;
  height: 170px;
}

.feature-card--medium .card-icon {
  font-size: 28px;
  margin-bottom: 20px;
}

.feature-card--medium .card-icon-img {
  width: 60px;
  height: 60px;
}

.feature-card--medium .card-title {
  font-size: 15px;
  margin-bottom: 6px;
}

.feature-card--medium .card-description {
  font-size: 12px;
  line-height: 1.5;
}

.feature-card--medium .card-arrow {
  font-size: 16px;
  margin-top: 8px;
}

.feature-card--medium .arrow-img {
  width: 18px;
  height: 18px;
}

/* large */
.feature-card--large {
  padding: 24px 24px;
  height: 220px;
}

.feature-card--large .card-icon {
  font-size: 38px;
  margin-bottom: 30px;
}

.feature-card--large .card-icon-img {
  width: 80px;
  height: 80px;
}

.feature-card--large .card-title {
  font-size: 18px;
  margin-bottom: 8px;
}

.feature-card--large .card-description {
  font-size: 14px;
  line-height: 1.6;
}

.feature-card--large .card-arrow {
  font-size: 20px;
  margin-top: 12px;
}

.feature-card--large .arrow-img {
  width: 24px;
  height: 24px;
}

/* ========== 图标 ========== */
.card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.card-icon-img {
  display: block;
  object-fit: contain;
}


/* ========== 标题 ========== */
.card-title {
  font-weight: 600;
  color: var(--text-primary, #1a1a2e);
  margin: 0;
  flex-shrink: 0;
}

/* ========== 描述（两行省略） ========== */
.card-description {
  color: var(--text-secondary, #666);
  margin: 0;
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
}

/* ========== 箭头 ========== */
.card-arrow {
  align-self: flex-end;
  color: var(--text-muted, #999);
  transition: transform 0.2s, color 0.2s;
  flex-shrink: 0;
  line-height: 1;
}

.feature-card:hover .card-arrow {
  transform: translateX(4px);
  color: var(--accent, #5b8def);
}

.arrow-img {
  display: block;
  object-fit: contain;
  transition: transform 0.2s;
}

.feature-card:hover .arrow-img {
  transform: translateX(4px);
}

/* ========== 移动端适配 ========== */
@media (max-width: 768px) {
  .feature-card--small {
    height: 100px;
    padding: 10px 12px;
  }

  .feature-card--medium {
    height: 140px;
    padding: 14px 14px;
  }

  .feature-card--large {
    height: 180px;
    padding: 16px 16px;
  }

  .feature-card--small .card-icon {
    font-size: 18px;
    margin-bottom: 4px;
  }

  .feature-card--medium .card-icon {
    font-size: 24px;
    margin-bottom: 8px;
  }

  .feature-card--large .card-icon {
    font-size: 30px;
    margin-bottom: 10px;
  }

  .feature-card--small .card-icon-img {
    width: 24px;
    height: 24px;
  }

  .feature-card--medium .card-icon-img {
    width: 34px;
    height: 34px;
  }

  .feature-card--large .card-icon-img {
    width: 44px;
    height: 44px;
  }

  .feature-card--small .card-title {
    font-size: 12px;
  }

  .feature-card--medium .card-title {
    font-size: 14px;
  }

  .feature-card--large .card-title {
    font-size: 16px;
  }

  .feature-card--small .card-description {
    font-size: 10px;
  }

  .feature-card--medium .card-description {
    font-size: 11px;
  }

  .feature-card--large .card-description {
    font-size: 12px;
  }

  .feature-card--small .card-arrow {
    font-size: 10px;
    margin-top: 2px;
  }

  .feature-card--medium .card-arrow {
    font-size: 14px;
    margin-top: 6px;
  }

  .feature-card--large .card-arrow {
    font-size: 16px;
    margin-top: 8px;
  }

  .feature-card--small .arrow-img {
    width: 12px;
    height: 12px;
  }

  .feature-card--medium .arrow-img {
    width: 16px;
    height: 16px;
  }

  .feature-card--large .arrow-img {
    width: 20px;
    height: 20px;
  }
}
</style>